import cx from 'classnames'
import { Popover } from 'components/common/Popover'
import { useEffect, useState } from 'react'
import { PropsWithClassName } from 'types'

type ButtonState = {
  label: string
  value: string
  disabled?: boolean
  tooltipText?: string | null
}

type Props = {
  className?: string
  value?: string
  leftState: ButtonState
  rightState: ButtonState
  danger?: boolean
  onChange?: (value: string) => void
  activeBuy?: boolean
  activeSell?: boolean
}

export const ToggleButton = ({
  className,
  value,
  leftState,
  rightState,
  onChange,
  danger,
  activeBuy,
  activeSell,
}: Props) => {
  const [internalValue, setInternalValue] = useState(value ?? leftState.value)

  useEffect(() => {
    if (activeBuy) {
      setInternalValue(rightState.value)
    }
  }, [activeBuy, rightState.value])

  useEffect(() => {
    if (activeSell) {
      setInternalValue(rightState.value)
    }
  }, [activeSell, rightState.value])

  useEffect(() => {
    setInternalValue(value ?? leftState.value)
  }, [value, leftState.value])

  return (
    <div
      className={cx(
        `relative grid h-[50px] grid-cols-2 rounded-full border border-solid border-cool-300  text-sm font-medium lg:text-base ${className}`,
        {
          'bg-cool-400':
            (internalValue === leftState.value && rightState.disabled) ||
            (internalValue === rightState.value && leftState.disabled),
          'bg-cool-50':
            (internalValue === leftState.value && !rightState.disabled) ||
            (internalValue === rightState.value && !leftState.disabled),
        }
      )}
    >
      <Button
        state={leftState}
        className={cx(
          'z-10 flex h-full w-full flex-1 items-center justify-center rounded-full transition-colors duration-300',
          {
            'peer text-cool-100': internalValue === leftState.value,
            'text-cool-500': leftState.disabled,
            'cursor-pointer': !leftState.disabled,
            'cursor-disabled': leftState.disabled,
          }
        )}
        onChange={value => {
          setInternalValue(value)
          onChange?.(value)
        }}
      />

      <Button
        state={rightState}
        className={cx(
          'z-10 flex h-full w-full flex-1 items-center justify-center rounded-full transition-colors duration-300',
          {
            'peer text-cool-100': internalValue === rightState.value,
            'text-cool-500': rightState.disabled,
            'cursor-pointer': !rightState.disabled,
            'cursor-disabled': rightState.disabled,
          }
        )}
        onChange={value => {
          setInternalValue(value)
          onChange?.(value)
        }}
      />

      <div
        className={cx(
          'absolute left-0 flex h-full w-1/2 justify-center rounded-full text-cool-150 transition-all duration-300',
          internalValue === leftState.value
            ? 'translate-x-0'
            : 'translate-x-full',
          {
            'bg-danger-dark peer-hover:bg-danger-default': danger,
            'bg-success-default peer-hover:bg-success-dark': !danger,
          }
        )}
      />
    </div>
  )
}

interface ButtonProps extends PropsWithClassName {
  state: ButtonState
  onChange: (value: string) => void
}

function Button({ state, onChange, className }: ButtonProps) {
  const onClick = () => {
    if (!state.disabled) {
      onChange(state.value)
    }
  }

  return (
    <>
      {state.tooltipText ? (
        <Popover
          content={state.tooltipText}
          className={className}
          triggerClassName='z-10 outline-none'
          side='top'
        >
          <button
            type='button'
            data-e2e={`${state.value}-button`}
            className={className}
            onClick={onClick}
          >
            {state.label}
          </button>
        </Popover>
      ) : (
        <button
          type='button'
          data-e2e={`${state.value}-button`}
          className={className}
          onClick={onClick}
        >
          {state.label}
        </button>
      )}
    </>
  )
}
